import { Component } from 'react';
import { getSentry } from './monitoring.js';
/**
 * Generic error boundary that reports errors to Sentry
 * Use this for app-level error boundaries
 */
export class AppErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null };
    }
    /**
     * Normalize any thrown value to an Error instance.
     * This prevents "Object captured as exception" issues in Sentry
     * when non-Error objects are thrown (e.g., API response objects).
     */
    static normalizeError(error) {
        if (error instanceof Error) {
            return error;
        }
        // Handle plain objects with message/error properties (common API error format)
        if (error !== null && typeof error === 'object') {
            const errorObj = error;
            const message = typeof errorObj.message === 'string'
                ? errorObj.message
                : typeof errorObj.error === 'string'
                    ? errorObj.error
                    : JSON.stringify(error);
            const normalizedError = new Error(message);
            normalizedError.name = 'NonErrorException';
            // Preserve original object for debugging
            normalizedError.originalValue = error;
            return normalizedError;
        }
        // Handle primitives
        return new Error(String(error));
    }
    static getDerivedStateFromError(error) {
        return { hasError: true, error: AppErrorBoundary.normalizeError(error) };
    }
    componentDidCatch(error, errorInfo) {
        const normalizedError = AppErrorBoundary.normalizeError(error);
        console.error('AppErrorBoundary caught an error:', normalizedError, errorInfo);
        // Capture current URL and component name
        const url = typeof window !== 'undefined' ? window.location.href : 'unknown';
        const componentName = this.props.componentName || 'unknown';
        // Report to Sentry if available
        const sentry = getSentry();
        if (sentry) {
            sentry.captureException(normalizedError, {
                level: 'error',
                fingerprint: ['app-error-boundary', componentName, normalizedError.name],
                tags: {
                    error_boundary: 'app',
                    component_name: componentName,
                    url,
                    ...this.props.sentryTags,
                },
                contexts: {
                    react: {
                        componentStack: errorInfo.componentStack,
                    },
                    app_info: {
                        component_name: componentName,
                        url,
                        pathname: typeof window !== 'undefined'
                            ? window.location.pathname
                            : 'unknown',
                        search: typeof window !== 'undefined' ? window.location.search : '',
                    },
                    ...(this.props.sentryContext
                        ? { custom: this.props.sentryContext }
                        : {}),
                },
            });
        }
        // Call custom error handler if provided
        this.props.onError?.(normalizedError, errorInfo);
    }
    resetError = () => {
        this.setState({ hasError: false, error: null });
    };
    render() {
        if (this.state.hasError && this.state.error) {
            return this.props.fallback({
                error: this.state.error,
                resetError: this.resetError,
            });
        }
        return this.props.children;
    }
}
//# sourceMappingURL=error-boundary.js.map